/**
 * "Item added to cart" popup — window.MiniCartPopup.
 *
 * Self-contained, no custom element (matches this theme's existing mixed
 * style — off-road.js is plain script-scope code, cart-drawer.js uses
 * custom elements; a plain module is simplest here and avoids solving
 * registration-order issues across two separate layouts).
 *
 * Deliberately independent of the existing cart-drawer/cart.js — this does
 * not open, read from, or write to that component at all. It is opened
 * from assets/productoption.js's bcpo add-to-cart handler only; simple
 * products keep using the existing drawer exactly as before.
 */
(function () {
  'use strict';

  var popupEl = null;
  var itemsEl = null;
  var footerEl = null;
  var totalEl = null;
  var listenersAttached = false;
  var currentAnchor = null;
  var layoutObserver = null;

  function fmtMoney(cents, currencyCode) {
    var amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currencyCode || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + amount.toFixed(2);
    }
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function ensure() {
    if (popupEl) return popupEl;
    popupEl = document.getElementById('MiniCartPopup');
    if (!popupEl) return null;
    itemsEl = popupEl.querySelector('[data-mcp-items]');
    footerEl = popupEl.querySelector('[data-mcp-footer]');
    totalEl = popupEl.querySelector('[data-mcp-total]');
    attachListeners();
    return popupEl;
  }

  function attachListeners() {
    if (listenersAttached || !popupEl) return;
    listenersAttached = true;

    popupEl.addEventListener('click', function (e) {
      if (e.target.closest('[data-mcp-close]')) {
        close();
        return;
      }
      // Checkout navigates from here rather than being an <a href="/checkout">.
      // The terms-and-conditions app hunts for checkout links and reparents
      // them into its own wrapper, which dragged this button out of the
      // two-column button grid. A plain button gives it nothing to match on.
      if (e.target.closest('[data-mcp-checkout]')) {
        window.location.href = '/checkout';
        return;
      }
      var removeBtn = e.target.closest('[data-mcp-remove]');
      if (removeBtn) handleRemove(removeBtn);
    });

    // Outside click closes it — but a click on the cart icon that opened
    // it (or the other design's icon) shouldn't immediately re-trigger a
    // flash-open-then-close in the same tick.
    document.addEventListener('click', function (e) {
      if (!popupEl.classList.contains('is-open')) return;
      if (popupEl.contains(e.target)) return;
      if (e.target.closest('#cart-icon-bubble, #orMobileCartIcon')) return;
      close();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    window.addEventListener('resize', close);
  }

  /**
   * The header cart icon to hang the popup off.
   *
   * The header renders two — #cart-icon-bubble for desktop and
   * #orMobileCartIcon for the narrow layout — and only one is shown at a time.
   * This used to choose with `offsetParent !== null`, which is not the same
   * question as "is this on screen": it stays non-null for elements that are
   * laid out but scrolled away, and it is null for anything inside a fixed
   * ancestor. Picking a hidden one yields an all-zero rect, which position()
   * then reads as "anchor is off screen" and pins the popup to the very top.
   *
   * Chosen by geometry instead, in order of preference:
   *   1. rendered AND currently on screen  — anchor under it
   *   2. rendered but scrolled out of view — keep its column, pin vertically
   *   3. nothing rendered                  — corner fallback
   */
  function visibleCartIcon() {
    var candidates = document.querySelectorAll('#cart-icon-bubble, #orMobileCartIcon');
    var rendered = null;
    for (var i = 0; i < candidates.length; i++) {
      var r = candidates[i].getBoundingClientRect();
      if (r.width === 0 && r.height === 0) continue; // display:none or empty
      if (r.bottom > 0 && r.top < window.innerHeight) return candidates[i];
      if (!rendered) rendered = candidates[i];
    }
    return rendered || null;
  }

  /**
   * Place the popup under the header cart icon — but never off screen.
   *
   * The popup is position:fixed, so these are viewport coordinates. The header
   * is NOT sticky (only the mobile nav drawer is fixed), so on a long product
   * page the cart icon has usually scrolled out of view by the time the
   * shopper reaches Add to Cart. Anchoring straight to it then produced a
   * negative top: the popup rendered above the top of the window, and because
   * it is fixed it stayed there when the shopper scrolled back up. All that
   * was visible was its bottom edge overlapping the header.
   *
   * So: anchor to the icon while the icon is actually on screen, otherwise pin
   * to the top-right. Either way the result is clamped so the popup is fully
   * within the viewport.
   */
  function position(anchorEl) {
    if (!popupEl || window.innerWidth <= 480) return; // narrow-viewport CSS media query takes over

    var rect = anchorEl ? anchorEl.getBoundingClientRect() : null;

    /* Horizontal alignment comes from the icon whenever there is one, even if
       the icon has scrolled out of view: the page only scrolls vertically, so
       the icon's left/right edges stay where they are. That keeps the popup
       sitting in the cart icon's column rather than drifting to a generic
       corner. Only the vertical position has to cope with the icon leaving
       the screen. */
    var right = rect ? Math.round(window.innerWidth - rect.right - 12) : 12;

    var anchorVerticallyOnScreen = rect && rect.bottom > 0 && rect.top < window.innerHeight;
    var top = anchorVerticallyOnScreen ? Math.round(rect.bottom + 14) : 14;

    /* Measured after the caller has unhidden the popup, so these are real. */
    var h = popupEl.offsetHeight || 0;
    var w = popupEl.offsetWidth || 0;

    var maxTop = Math.max(12, window.innerHeight - h - 12);
    var maxRight = Math.max(12, window.innerWidth - w - 12);

    popupEl.style.left = '';
    popupEl.style.top = Math.min(Math.max(top, 12), maxTop) + 'px';
    popupEl.style.right = Math.min(Math.max(right, 12), maxRight) + 'px';
  }

  function renderItems(cart) {
    if (!itemsEl) return;
    var items = (cart && cart.items) || [];

    if (!items.length) {
      itemsEl.innerHTML = '<div class="mini-cart-popup__empty">Your cart is empty.</div>';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }
    if (footerEl) footerEl.style.display = '';

    // Row structure mirrors the approved mockup: image, then uppercase title,
    // unit price suffixed "EACH", quantity on its own line, then Remove.
    itemsEl.innerHTML = items
      .map(function (item, idx) {
        return (
          '<div class="mini-cart-popup__item">' +
          '<img src="' + escapeHtml(item.image || '') + '" alt="" loading="lazy">' +
          '<div>' +
          '<div class="mini-cart-popup__item-name">' + escapeHtml(item.product_title || item.title) + '</div>' +
          '<div class="mini-cart-popup__item-price">' + fmtMoney(item.price, cart.currency) + ' EACH</div>' +
          '<div class="mini-cart-popup__item-qty">Qty: ' + item.quantity + '</div>' +
          '<button type="button" class="mini-cart-popup__item-remove" data-mcp-remove data-line="' + (idx + 1) + '">Remove</button>' +
          '</div>' +
          '</div>'
        );
      })
      .join('');

    if (totalEl) {
      totalEl.innerHTML = 'total: <b>' + fmtMoney(cart.total_price, cart.currency) + '</b>';
    }
  }

  function handleRemove(btn) {
    var line = parseInt(btn.getAttribute('data-line'), 10);
    if (!line) return;
    btn.disabled = true;
    btn.textContent = 'Removing…';

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: line, quantity: 0 })
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        renderItems(cart);
        updateHeaderBadges(cart.item_count);
        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'mini-cart-popup', cart: cart });
        }
      })
      .catch(function () {
        btn.disabled = false;
        btn.textContent = 'Remove';
      });
  }

  /**
   * Put the Checkout button back if a third-party script has moved it.
   *
   * The store's terms-and-conditions app reparents checkout controls into its
   * own wrapper so it can gate them. When it does that here it drags the
   * button out of .mini-cart-popup__actions, and the two-column layout
   * collapses into two stacked full-width rows. Switching the button away from
   * an <a href="/checkout"> should stop it being matched at all; this is the
   * backstop in case the app identifies it some other way, since we can't see
   * its source and it can change without notice.
   */
  function enforceLayout() {
    if (!popupEl) return;
    var actions = popupEl.querySelector('.mini-cart-popup__actions');
    var checkout = popupEl.querySelector('[data-mcp-checkout]');
    if (!actions || !checkout) return;
    if (checkout.parentElement !== actions) actions.appendChild(checkout);
  }

  function watchLayout() {
    if (!popupEl || layoutObserver) return;
    layoutObserver = new MutationObserver(function () {
      // Detach while correcting so our own move doesn't retrigger the observer.
      layoutObserver.disconnect();
      enforceLayout();
      layoutObserver.observe(popupEl, { childList: true, subtree: true });
    });
    layoutObserver.observe(popupEl, { childList: true, subtree: true });
  }

  function open(anchorEl, cart) {
    if (!ensure()) return;
    currentAnchor = anchorEl || visibleCartIcon();
    renderItems(cart);
    popupEl.hidden = false;
    popupEl.classList.add('is-open');
    position(currentAnchor);
    enforceLayout();
    watchLayout();
  }

  function close() {
    if (!popupEl) return;
    popupEl.classList.remove('is-open');
    popupEl.hidden = true;
  }

  function updateHeaderBadges(itemCount) {
    // Off-road: <span class="cart-badge"> is always present in the DOM on
    // both the desktop (#cart-icon-bubble) and mobile (#orMobileCartIcon)
    // header icons — just update the text.
    document
      .querySelectorAll('#cart-icon-bubble .cart-badge, #orMobileCartIcon .cart-badge')
      .forEach(function (el) { el.textContent = itemCount; });

    // Main site's #cart-icon-bubble uses a different badge shape
    // (.cart-count-bubble), Liquid-conditional on cart != empty at page
    // load — on a session's first add it may not exist in the DOM yet and
    // has to be created client-side. Skip entirely if the off-road badge
    // (handled above already) is what's actually present here.
    var link = document.getElementById('cart-icon-bubble');
    if (!link || link.querySelector('.cart-badge')) return;

    var bubble = link.querySelector('.cart-count-bubble');
    if (itemCount >= 100) {
      // Dawn's own cart-icon-bubble.liquid only shows a number under 100.
      if (bubble) bubble.remove();
      return;
    }
    if (bubble) {
      var span = bubble.querySelector('span[aria-hidden]');
      if (span) {
        span.textContent = itemCount;
      } else {
        span = document.createElement('span');
        span.setAttribute('aria-hidden', 'true');
        span.textContent = itemCount;
        bubble.insertBefore(span, bubble.firstChild);
      }
    } else if (itemCount > 0) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true">' + itemCount + '</span>';
      link.appendChild(bubble);
    }
  }

  /**
   * Route the theme's own add-to-cart through this popup instead of the
   * slide-in drawer, so every product gives the same confirmation.
   *
   * Products WITH the bcpo options configurator already come here, via
   * productoption.js. Products without one are handled by the stock
   * product-form.js, which finishes by calling CartDrawer.renderContents() —
   * that refreshes the drawer's markup and then opens the drawer.
   *
   * This replaces renderContents so it still refreshes the drawer's contents
   * (so the drawer is correct whenever the shopper opens it from the cart
   * icon) but opens the popup instead of the drawer. product-form.js is left
   * completely alone, which matters: its error handling still runs, so a
   * sold-out product still reports itself rather than failing silently.
   *
   * Only the add-to-cart path goes through renderContents. Quantity edits and
   * removals inside the drawer use CartItems' own section rendering, so they
   * are unaffected and the drawer keeps behaving normally once open.
   */
  /**
   * Put the drawer's empty/not-empty flag where the markup and CSS actually
   * keep it.
   *
   * snippets/cart-drawer.liquid sets `is-empty` in TWO places — on the
   * <cart-drawer> root and on <cart-drawer-items> — and
   * component-cart-drawer.css keys its rules off `cart-drawer-items.is-empty`.
   * Dawn's own cart-drawer.js (and, until now, the patch below) only cleared it
   * from `.drawer__inner`, an element that never carries it. So a drawer that
   * was rendered empty stayed flagged empty forever: add an item and the
   * drawer showed "Your Cart is Empty" and the new line item at the same time,
   * with the header still reading 0 items.
   *
   * Driven from the real /cart.js item count rather than the add-to-cart
   * response, so it is right regardless of which path triggered the update.
   */
  function setDrawerEmptyState(drawer, itemCount) {
    if (!drawer) return;
    var empty = itemCount === 0;
    drawer.classList.toggle('is-empty', empty);
    var items = drawer.querySelector('cart-drawer-items');
    if (items) items.classList.toggle('is-empty', empty);
    var inner = drawer.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', empty);
  }

  function routeAddToCartThroughPopup() {
    if (typeof CartDrawer === 'undefined' || !CartDrawer.prototype) return;
    if (CartDrawer.prototype.__mcpPatched) return;
    CartDrawer.prototype.__mcpPatched = true;

    CartDrawer.prototype.renderContents = function (parsedState) {
      var drawer = this;
      this.productId = parsedState.id;

      // Same section refresh the original does, but tolerant of a missing
      // element or section rather than throwing — off-road removes the
      // cart-icon-bubble entry because its header owns that markup.
      this.getSectionsToRender().forEach(function (section) {
        var el = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        var html = parsedState.sections && parsedState.sections[section.id];
        if (!el || !html) return;
        try {
          el.innerHTML = drawer.getSectionInnerHTML(html, section.selector);
        } catch (e) {
          /* leave the existing markup alone if the section can't be parsed */
        }
      });

      // ...then the popup, rather than this.open().
      fetch('/cart.js')
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          setDrawerEmptyState(drawer, cart.item_count);
          updateHeaderBadges(cart.item_count);
          open(null, cart);
        })
        .catch(function () {
          // If the cart can't be read, fall back to the drawer so the add is
          // still visibly confirmed rather than appearing to do nothing.
          try { drawer.open(); } catch (e) { /* no-op */ }
        });
    };
  }

  // Deliberately waits for DOMContentLoaded rather than running inline: this
  // file is loaded before cart-drawer.js in both layouts (head vs body, but
  // deferred scripts run in document order either way), so CartDrawer does not
  // exist yet at this point. Every deferred script has executed by
  // DOMContentLoaded. The load listener is a backstop for the case where this
  // file is somehow evaluated after that has already fired; __mcpPatched makes
  // running twice harmless.
  if (document.readyState === 'complete') {
    routeAddToCartThroughPopup();
  } else {
    document.addEventListener('DOMContentLoaded', routeAddToCartThroughPopup);
    window.addEventListener('load', routeAddToCartThroughPopup);
  }

  window.MiniCartPopup = {
    open: open,
    close: close,
    renderItems: renderItems,
    updateHeaderBadges: updateHeaderBadges
  };
})();
